import React, { useState, useEffect } from "react";
import {
    FaGraduationCap,
    FaUsers,
    FaHandshake,
    FaAward,
    FaCertificate,
    FaLightbulb,
} from "react-icons/fa";
import cisco from "../../assets/cisco.png";
import comptia from "../../assets/comptia.png";
import giccs from "../../assets/giccs_logo.svg";
import cmsApi from "../../services/cmsApi";
import { getImageUrl } from "../../utils/getImageUrl";

const defaultPartners = [
    { name: 'CompTIA', logoUrl: '', description: 'Leading IT certification and training provider', fallbackLogo: comptia },
    { name: 'GICCS', logoUrl: '', description: 'Global Intergrated Charitable Community Services', fallbackLogo: giccs },
    { name: 'Cisco Networking Academy', logoUrl: '', description: 'Global leader in networking and cybersecurity solutions', fallbackLogo: cisco },
];

const defaultValues = [
        {
            icon: (
                <FaGraduationCap className="text-4xl transition-all duration-500 group-hover:text-white" />
            ),
            title: "Excellence in Education",
            description:
                "To offer instruction in the various courses offered a level comparable to the best",
        },
        {
            icon: (
                <FaLightbulb className="text-4xl transition-all duration-500 group-hover:text-white" />
            ),
            title: "Future-Ready Learning",
            description:
                "To anticipate the future needs of the employment sector and plan course materials to suit those needs",
        },
        {
            icon: (
                <FaUsers className="text-4xl transition-all duration-500 group-hover:text-white" />
            ),
            title: "Independent Studies",
            description:
                "To provide facilities for independent studies and personalized learning experiences",
        },
        {
            icon: (
                <FaHandshake className="text-4xl transition-all duration-500 group-hover:text-white" />
            ),
            title: "Community Development",
            description:
                "To develop programs for lower, medium and higher educational institutions in the region",
        },
        {
            icon: (
                <FaCertificate className="text-4xl transition-all duration-500 group-hover:text-white" />
            ),
            title: "Faculty Development",
            description:
                "To train and assist teachers and prospective teaching faculty an ability to formulate curriculum planning",
        },
    ];

const AboutUs = () => {
    const [cmsData, setCmsData] = useState(null);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const result = await cmsApi.getPageComponents("about");
                if (result && result.success && result.components) {
                    const map = {};
                    result.components.forEach((c) => { map[c.componentName] = c; });
                    setCmsData(map);
                }
            } catch (e) {
                console.error("Failed to fetch about page CMS content:", e);
            }
        };
        fetchContent();
    }, []);

    const hero = cmsData?.hero || {};
    const history = cmsData?.history || {};
    const commitment = cmsData?.commitment || {};
    const partnerships = cmsData?.partnerships || {};
    const partnershipItems = (() => {
        if (partnerships.settings) {
            try {
                const parsed = JSON.parse(partnerships.settings || "[]");
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed.filter(p => p?.name?.trim() || p?.description?.trim());
                }
            } catch {
                /* fall through to defaults */
            }
        }
        return defaultPartners;
    })();
    const values = cmsData?.values
        ? (() => { try { return JSON.parse(cmsData.values.settings || "[]"); } catch { return defaultValues; } })()
        : defaultValues;

    return (
        <div className="min-h-screen bg-gray-50 overflow-hidden">
            {/* Hero Section */}
            {(() => {
                const heroImageUrl = hero.mediaUrl ? getImageUrl(hero.mediaUrl) : null;

                return heroImageUrl ? (
                    <section
                        className="text-white py-20 relative bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: `url(${heroImageUrl})` }}
                    >
                        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                        <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 animate-fade-in-down">
                                {hero.title || "About JR Institute of Information Technology"}
                            </h1>
                            <p className="text-lg sm:text-xl mb-8 max-w-3xl mx-auto animate-fade-in-up">
                                {hero.subtitle || "Empowering minds through technology education since 1999. Building tomorrow's IT professionals today."}
                            </p>
                            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-6 max-w-2xl mx-auto">
                                <h2 className="text-xl sm:text-2xl font-semibold mb-4">
                                    ELIMU NA UJUZI
                                </h2>
                                <p className="text-base sm:text-lg">
                                    Education and Skills - Our Foundation for Excellence
                                </p>
                            </div>
                        </div>
                    </section>
                ) : (
                    <section className="bg-gradient-to-r from-custom-pink via-purple-700 to-purple-600 text-white py-20 relative">
                        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                        <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 animate-fade-in-down">
                                {hero.title || "About JR Institute of Information Technology"}
                            </h1>
                            <p className="text-lg sm:text-xl mb-8 max-w-3xl mx-auto animate-fade-in-up">
                                {hero.subtitle || "Empowering minds through technology education since 1999. Building tomorrow's IT professionals today."}
                            </p>
                            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-6 max-w-2xl mx-auto animate-pulse-glow">
                                <h2 className="text-xl sm:text-2xl font-semibold mb-4">
                                    ELIMU NA UJUZI
                                </h2>
                                <p className="text-base sm:text-lg">
                                    Education and Skills - Our Foundation for Excellence
                                </p>
                            </div>
                        </div>
                        <div className="absolute top-10 left-10 w-20 h-20 bg-white bg-opacity-10 rounded-full animate-float"></div>
                        <div className="absolute bottom-10 right-10 w-16 h-16 bg-gold bg-opacity-20 rounded-full animate-float-delayed"></div>
                        <div className="absolute top-1/2 right-20 w-12 h-12 bg-purple-300 bg-opacity-20 rounded-full animate-bounce-slow"></div>
                    </section>
                );
            })()}

            {/* Our Journey Section */}
            <section className="py-16 sm:py-20 bg-white relative">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-left mb-12 sm:mb-16">
                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4 animate-fade-in">
                                {history.title || "Let us introduce ourselves"}
                            </h2>
                            <div className="w-24 h-1 bg-gold mb-8 animate-slide-in"></div>
                        </div>

                        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-center">
                            <div className="lg:col-span-2 space-y-6 text-[1.2rem] leading-relaxed tracking-wider text-gray-700 animate-slide-in-left">
                                {(history.content || `JR Institute of Information Technology (JRIIT) history is traced back in 1999 when JR Electronics LTD, a company dealing with IT communication and network branched itself to give a start to an education entity called JR Institute of Information Technology (JRIIT).

Initially JRIIT scope of action was simply limited to train the workers (and candidates to employment) of the parental company. But soon JRIIT transformed itself into a fully Educational Institute embracing more local young people who were eager to learn new IT skills.

JRIIT is fully registered under the National Council for Technical and Vocational Education and Training (NACTVET) with the registration number REG/EOS/017. Further, JRIIT has established partnership with other vocational entities such as CompTIA, Prometric and Pearson Vue.`).split("\n\n").map((paragraph, idx) => (
                                    <p key={idx}>{paragraph}</p>
                                ))}
                            </div>

                            <div className="flex justify-center animate-slide-in-right">
                                <div className="bg-gradient-to-br from-custom-pink to-purple-600 p-8 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300 hover:rotate-3">
                                    <div className="text-center text-white">
                                        <FaAward className="text-6xl mx-auto mb-4 animate-bounce-slow" />
                                        <h3 className="text-xl sm:text-2xl font-bold mb-4">
                                            Officially Registered
                                        </h3>
                                        <div className="bg-white bg-opacity-20 backdrop-blur-sm p-4 rounded-lg">
                                            <p className="text-lg font-semibold text-yellow-300">
                                                NACTVET Registration
                                            </p>
                                            <p className="text-2xl font-bold">REG/EOS/017</p>
                                            <p className="text-sm mt-2 text-gray-200">
                                                National Council for Technical and Vocational Education and Training
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-16 sm:py-20 bg-gradient-to-br from-gray-50 to-purple-50 relative overflow-hidden">
                {/* Background Animation Elements */}
                <div className="absolute top-0 left-0 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div className="absolute top-0 right-0 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-32 left-20 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

                <div className="container mx-auto px-4 sm:px-6 relative z-10">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4 animate-fade-in">
                            Our Core Values
                        </h2>
                        <div className="w-24 h-1 bg-gold mx-auto mb-6 animate-slide-in"></div>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto animate-fade-in-up">
                            Our commitment to excellence in teaching, learning, and research
                            drives us to serve our students and community with unwavering
                            dedication.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        {values.map((value, index) => (
                            <div
                                key={index}
                                className="group  relative animate-fade-in-up"
                                style={{ animationDelay: `${index * 150}ms` }}
                            >
                                {/* U-shape Card with Pentagon hole */}
                                <div className="relative pt-16 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
                                    {/* Pentagon hole at top center */}
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4">
                                        <div className="w-16 h-16 mt-14 bg-gradient-to-br from-purple-500 to-custom-pink rounded-full group-hover:scale-110 transition-all duration-500 flex items-center justify-center shadow-lg">
                                            <div className="transform  group-hover:scale-x-[-1] transition-all duration-500">
                                                {value.icon}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card Content */}
                                    <div className="pt-12 pb-8 px-6 text-center">
                                        <h3 className="text-xl font-semibold text-gray-800 mb-4 group-hover:text-purple-700 transition-colors duration-300">
                                            {value.title}
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                                            {value.description}
                                        </p>
                                    </div>

                                    {/* Hover effect gradient overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-custom-pink opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-2xl"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Partnerships Section */}
            <section className="py-16 sm:py-20 bg-white relative">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4 animate-fade-in">
                            {partnerships.title || "Strategic Partnerships"}
                        </h2>
                        <div className="w-24 h-1 bg-gold mx-auto mb-6 animate-slide-in"></div>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto animate-fade-in-up">
                            {partnerships.subtitle || "We've established strong partnerships with leading technology and certification entities to enhance our educational offerings and industry recognition."}
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {partnershipItems.map((partner, index) => {
                            const logoSrc = partner.logoUrl
                                ? getImageUrl(partner.logoUrl)
                                : partner.fallbackLogo || defaultPartners[index]?.fallbackLogo;

                            return (
                                <div
                                    key={`${partner.name || 'partner'}-${index}`}
                                    className="group bg-white p-8 rounded-2xl text-center hover:transform hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl animate-fade-in-up border border-gray-200"
                                    style={{ animationDelay: `${index * 200}ms` }}
                                >
                                    <div className="mb-6">
                                        <div className="w-32 h-20 mx-auto bg-gray-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-gray-100 transition-all duration-500 overflow-hidden">
                                            {logoSrc && (
                                                <img
                                                    src={logoSrc}
                                                    alt={`${partner.name || 'Partner'} Logo`}
                                                    className="max-w-full max-h-full object-contain group-hover:scale-110 transition-all duration-500"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {partner.name && (
                                        <h3 className="text-lg font-bold mb-2 text-gray-800 group-hover:text-custom-pink transition-colors duration-300">
                                            {partner.name}
                                        </h3>
                                    )}
                                    <p className="text-gray-600 group-hover:text-gray-800 transition-colors duration-300">
                                        {partner.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Mission Statement */}
            <section className="py-16 sm:py-20 bg-gradient-to-r from-custom-pink/70 via-custom-pink to-black text-white relative overflow-hidden">
                {/* Animated background elements */}
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-2 h-2 bg-gold rounded-full animate-ping"></div>
                    <div className="absolute top-20 right-20 w-3 h-3 bg-custom-pink rounded-full animate-pulse"></div>
                    <div className="absolute bottom-10 left-20 w-2 h-2 bg-blue-400 rounded-full animate-ping animation-delay-2000"></div>
                    <div className="absolute bottom-20 right-10 w-3 h-3 bg-purple-400 rounded-full animate-pulse animation-delay-4000"></div>
                </div>

                <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-8 animate-fade-in">
                            {commitment.title || "Our Commitment"}
                        </h2>
                        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 sm:p-8 animate-fade-in-up">
                            <p className="text-lg sm:text-xl leading-relaxed">
                                {commitment.content || "At JRIIT, we are committed to excellence in teaching, learning, and research. We strive to anticipate the future needs of the employment sector while providing world-class education that empowers our students to become leaders in the Information Technology field."}
                            </p>
                        </div>

                        <div className="grid sm:grid-cols-3 gap-8 mt-12">
                            <div className="text-center animate-count-up">
                                <div className="text-3xl sm:text-4xl font-bold text-gold mb-2">
                                    25+
                                </div>
                                <p className="text-gray-300">Years of Excellence</p>
                            </div>
                            <div
                                className="text-center animate-count-up"
                                style={{ animationDelay: "200ms" }}
                            >
                                <div className="text-3xl sm:text-4xl font-bold text-gold mb-2">
                                    1000+
                                </div>
                                <p className="text-gray-300">Graduates</p>
                            </div>
                            <div
                                className="text-center animate-count-up"
                                style={{ animationDelay: "400ms" }}
                            >
                                <div className="text-3xl sm:text-4xl font-bold text-gold mb-2">
                                    {partnershipItems.length}
                                </div>
                                <p className="text-gray-300">Strategic Partners</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            {/*<section className="py-16 sm:py-20 bg-gradient-to-br from-custom-pink via-purple-600 to-blue-600 text-white relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl opacity-10 animate-blob"></div>
                    <div className="absolute bottom-0 right-0 w-72 h-72 bg-gold rounded-full mix-blend-overlay filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
                </div>

                <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-6 animate-fade-in">
                        Ready to Start Your IT Journey?
                    </h2>
                    <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto animate-fade-in-up">
                        Join JRIIT and become part of a community that's shaping the future
                        of technology education.
                    </p>
                    <div
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up"
                        style={{ animationDelay: "400ms" }}
                    >
                        <Link to="/admissions" className="w-full sm:w-auto bg-gold text-white px-8 py-3 rounded-full font-semibold hover:bg-yellow-600 hover:transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-center">
                            Apply Now
                        </Link>
                        <Link to="/about" className="w-full sm:w-auto border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-custom-pink hover:transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-center">
                            Learn More
                        </Link>
                    </div>
                </div>
            </section>
            */}

            {/* Custom CSS for animations */}
            <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-in {
          from {
            opacity: 0;
            transform: scaleX(0);
          }
          to {
            opacity: 1;
            transform: scaleX(1);
          }
        }

        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes bounce-slow {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        @keyframes pulse-glow {
          0%,
          100% {
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
          }
          50% {
            box-shadow: 0 0 40px rgba(255, 255, 255, 0.5);
          }
        }

        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }

        @keyframes count-up {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-fade-in-down {
          animation: fade-in-down 1s ease-out forwards;
        }
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
        }
        .animate-slide-in {
          animation: slide-in 1s ease-out forwards;
        }
        .animate-slide-in-left {
          animation: slide-in-left 1s ease-out forwards;
        }
        .animate-slide-in-right {
          animation: slide-in-right 1s ease-out forwards;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 3s ease-in-out infinite 1s;
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animate-count-up {
          animation: count-up 1s ease-out forwards;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
        </div>
    );
};

export default AboutUs;
